import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Header, Form, Icon, List } from 'semantic-ui-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { RoundedCard, CompactList, CompactInput, CompactCheckbox, IconContainer } from './components/SharedStyles';
import TaskEditForm from './components/TaskEditForm';
import { formatFormDate, formatDueDate, isDueDateTodayOrEarlier } from './utils/dateUtils';
import { normalizeUrl, safeUrl } from './utils/urlUtils';
import { useTaskContext } from './context/TaskContext';
import * as api from './services/api';
import './DiditList.css';

const DiditList = ({ user, isCompactMode }) => {
  const { parentTask, childTasks, setChildTasks, loadTask, refreshChildren } = useTaskContext();
  const [editTask, setEditTask] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerEditValue, setHeaderEditValue] = useState('');
  const headerSavingRef = useRef(false);
  const [delegates, setDelegates] = useState([]);
  const [selectedDelegateUuids, setSelectedDelegateUuids] = useState([]);

  // Load task from URL on mount and when URL changes
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const parent = queryParams.get('p') || queryParams.get('parent');
    if (parent) {
      loadTask(parent);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect URL changes on every render (matching original behavior)
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const parent = queryParams.get('p') || queryParams.get('parent');
    if (parent && parent !== parentTask?.uuid) {
      loadTask(parent);
    }
  });

  // Load delegate roster when user is available
  useEffect(() => {
    if (user) {
      api.getDelegates().then(setDelegates).catch(err => console.error('Error loading delegates:', err));
    }
  }, [user]);

  const hasPermission = useCallback((permission) => {
    return user && user.Permissions && user.Permissions.includes(permission);
  }, [user]);

  const handleHeaderEdit = () => {
    if (parentTask && hasPermission('edit_parent_task')) {
      setHeaderEditValue(parentTask.label);
      setIsEditingHeader(true);
    }
  };

  const handleHeaderSave = async () => {
    if (headerSavingRef.current) return;
    headerSavingRef.current = true;
    const trimmed = headerEditValue.trim();
    if (!trimmed || trimmed === parentTask.label) {
      setIsEditingHeader(false);
      headerSavingRef.current = false;
      return;
    }
    try {
      await api.updateTask({ uuid: parentTask.uuid, label: trimmed });
      await loadTask(parentTask.uuid);
    } catch (err) {
      console.error('Error updating parent task label:', err);
    }
    setIsEditingHeader(false);
    headerSavingRef.current = false;
  };

  const handleHeaderKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setIsEditingHeader(false);
    }
  };

  const addChildTask = useCallback(async (parent, label) => {
    if (!label || !label.trim()) return;
    try {
      await api.createChildTask(parent, label);
      refreshChildren();
    } catch (err) {
      console.error('Error creating child task:', err);
    }
  }, [refreshChildren]);

  const handleUpdateTaskStatus = useCallback(async (uuid, status) => {
    setChildTasks(prev => prev.map(t => t.uuid === uuid ? { ...t, status } : t));
    try {
      await api.updateTaskStatus(uuid, status);
    } catch (err) {
      console.error('Error updating task status:', err);
      refreshChildren();
    }
  }, [setChildTasks, refreshChildren]);

  const handleEditClick = (item) => {
    const dueDate = item.due ? new Date(item.due) : null;
    const isValidDate = dueDate && !isNaN(dueDate.getTime()) && dueDate.getFullYear() > 1970;
    setEditTask(item.uuid);
    setFormData({
      label: item.label,
      content: item.content,
      url: item.url,
      due: isValidDate ? formatFormDate(item.due) : '',
      size: item.size,
    });
    setSelectedDelegateUuids((item.delegates || []).map(d => d.delegate_uuid));
  };

  const handleFormChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async () => {
    try {
      const updatedTask = {
        uuid: editTask,
        ...formData,
        due: formData.due ? new Date(formData.due).toISOString() : null,
      };
      await api.updateTask(updatedTask);
      await api.setTaskDelegates(editTask, selectedDelegateUuids);
      setEditTask(null);
      setFormData({});
      setSelectedDelegateUuids([]);
      refreshChildren();
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  const handleDeleteTask = async () => {
    try {
      await api.deleteTask(editTask);
      setEditTask(null);
      setFormData({});
      setSelectedDelegateUuids([]);
      refreshChildren();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const handleNewTaskSubmit = (e) => {
    e.preventDefault();
    const inputValue = e.target[0].value.trim();
    if (inputValue && parentTask) {
      addChildTask(parentTask.uuid, inputValue);
      e.target[0].value = '';
    }
  };

  const canEditChildTask = hasPermission('edit_child_task');
  const canSplitChildTask = hasPermission('ai_split_child_task');
  const canReorder = user && parentTask && (parentTask.owner === user.ID || parentTask.creator === user.ID);

  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const reordered = Array.from(childTasks);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setChildTasks(reordered);
    try {
      await api.reorderTasks(parentTask.uuid, reordered.map(t => t.uuid));
    } catch (err) {
      console.error('Error reordering tasks:', err);
      refreshChildren();
    }
  }, [childTasks, parentTask, setChildTasks, refreshChildren]);

  const renderNewTaskInput = () => {
    if (!parentTask || !canEditChildTask) return null;

    if (isCompactMode) {
      return (
        <Form onSubmit={handleNewTaskSubmit}>
          <CompactInput type="text" name="task" fluid placeholder="Create Task" />
        </Form>
      );
    }

    return (
      <RoundedCard key="new-input-card" raised>
        <Card.Content>
          <Card.Header>
            <Form onSubmit={handleNewTaskSubmit}>
              <input type="text" name="task" placeholder="Create Task" style={{ width: '100%', border: 'none', outline: 'none' }} />
            </Form>
          </Card.Header>
        </Card.Content>
      </RoundedCard>
    );
  };

  const renderCompactTaskItem = (item, index) => {
    return (
      <Draggable key={item.uuid} draggableId={item.uuid} index={index} isDragDisabled={!canReorder}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <List.Item>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {canReorder && (
                  <span {...provided.dragHandleProps} style={{ cursor: 'grab', marginRight: '6px', color: '#ccc', display: 'flex', alignItems: 'center' }}>
                    <Icon name="bars" size="small" style={{ margin: 0 }} />
                  </span>
                )}
                {parentTask?.isTemplate ? (
                  <span className="task-label" style={{ fontSize: '1rem' }}>{item.label}</span>
                ) : (
                  <CompactCheckbox
                    label={item.label}
                    onChange={() => handleUpdateTaskStatus(item.uuid, item.status === 1 ? 2 : 1)}
                    checked={item.status === 2}
                  />
                )}
                {item.delegates && item.delegates.length > 0 && (
                  <span style={{ marginLeft: 'auto', marginRight: '6px', display: 'flex', gap: '3px' }}>
                    {item.delegates.map(d => (
                      <span key={d.delegate_uuid} title={d.delegate_name} style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '22px', height: '22px', borderRadius: '50%',
                        backgroundColor: '#2185d0', color: 'white', fontSize: '0.7em', fontWeight: 'bold',
                      }}>
                        {d.delegate_name?.charAt(0).toUpperCase()}
                      </span>
                    ))}
                  </span>
                )}
                {item.due && formatDueDate(item.due) && (
                  <span className="due-date" style={{ color: isDueDateTodayOrEarlier(item.due) ? 'red' : 'inherit' }}>
                    {formatDueDate(item.due)}
                  </span>
                )}
                <IconContainer>
                  {canEditChildTask && (
                    <Icon>
                      <img width={16} height={16} src="/pen-circle.svg" alt="Edit" onClick={() => handleEditClick(item)} />
                    </Icon>
                  )}
                  {canSplitChildTask && (
                    <Icon>
                      <img width={16} height={16} src="/edit-wand.svg" alt="AI Split" />
                    </Icon>
                  )}
                </IconContainer>
              </div>
            </List.Item>
          </div>
        )}
      </Draggable>
    );
  };

  const renderCardTaskItem = (item) => {
    const style = {
      wordWrap: 'break-word',
      ...(item.status === 2 && { textDecorationLine: 'line-through' }),
    };

    return (
      <RoundedCard key={item.uuid} raised>
        <Card.Content>
          <Card.Header>
            <div style={style}>
              {item.url && safeUrl(item.url) ? (
                <a href={normalizeUrl(safeUrl(item.url))} target="_blank" rel="noopener noreferrer">{item.label}</a>
              ) : (
                item.label
              )}
            </div>
          </Card.Header>
          <Card.Description>
            <div style={style}>{item.content}</div>
          </Card.Description>
          {item.delegates && item.delegates.length > 0 && (
            <Card.Meta style={{ marginTop: '0.5em', color: '#2185d0' }}>
              {item.delegates.map(d => d.delegate_name).join(', ')}
            </Card.Meta>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {item.due && formatDueDate(item.due) && (
              <Card.Meta style={{ color: isDueDateTodayOrEarlier(item.due) ? 'red' : 'inherit' }}>
                {formatDueDate(item.due)}
              </Card.Meta>
            )}
            <Card.Meta textAlign="right">
              {!parentTask?.isTemplate && (
                <Icon onClick={() => handleUpdateTaskStatus(item.uuid, item.status === 1 ? 2 : 1)}>
                  <img width={16} height={16} src={item.status === 1 ? '/circle.svg' : '/check-circle.svg'} alt={item.status === 1 ? 'Unchecked' : 'Checked'} />
                </Icon>
              )}
              {canEditChildTask && (
                <Icon>
                  <img width={16} height={16} src="/pen-circle.svg" alt="Edit" onClick={() => handleEditClick(item)} />
                </Icon>
              )}
              {canSplitChildTask && (
                <Icon>
                  <img width={16} height={16} src="/edit-wand.svg" alt="AI Split" />
                </Icon>
              )}
            </Card.Meta>
          </div>
        </Card.Content>
      </RoundedCard>
    );
  };

  if (!parentTask) {
    const steps = [
      { icon: 'plus circle', title: 'Create a Task List', description: 'Tap the + button above to create a new task list.' },
      { icon: 'tasks', title: 'Add Tasks', description: 'Add items to your list that need to get done.' },
      { icon: 'share alternate', title: 'Delegate', description: 'Share your list with others so they can see and complete their tasks.' },
      { icon: 'check circle', title: 'Track Progress', description: 'See tasks get checked off as they are completed.' },
    ];

    return (
      <div style={{ textAlign: 'center', padding: '2em 0' }}>
        <Header as="h2" style={{ color: '#555', fontWeight: 'normal' }}>
          Get Started
        </Header>
        <Card.Group stackable doubling style={{ maxWidth: '600px', margin: '0 auto' }}>
          {steps.map((step, i) => (
            <RoundedCard key={i} fluid>
              <Card.Content>
                <Card.Header>
                  <Icon name={step.icon} color="blue" style={{ marginRight: '8px' }} />
                  {step.title}
                </Card.Header>
                <Card.Description style={{ marginTop: '0.5em', color: '#666' }}>
                  {step.description}
                </Card.Description>
              </Card.Content>
            </RoundedCard>
          ))}
        </Card.Group>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <Header as="h2" style={{ margin: '5px 0px 0px 0px', lineHeight: '1.2' }}>
        {isEditingHeader ? (
          <input
            type="text"
            value={headerEditValue}
            onChange={(e) => setHeaderEditValue(e.target.value)}
            onKeyDown={handleHeaderKeyDown}
            onBlur={handleHeaderSave}
            autoFocus
            style={{
              width: '100%',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              fontFamily: 'inherit',
              border: 'none',
              borderBottom: '2px solid #2185d0',
              outline: 'none',
              padding: '0',
              background: 'transparent',
            }}
          />
        ) : (
          <span
            onClick={handleHeaderEdit}
            style={{ cursor: hasPermission('edit_parent_task') ? 'pointer' : 'default' }}
            title={hasPermission('edit_parent_task') ? 'Click to edit' : undefined}
          >
            {parentTask.label}
          </span>
        )}
      </Header>

      <TaskEditForm
        open={editTask !== null}
        formData={formData}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCancel={() => { setEditTask(null); setSelectedDelegateUuids([]); }}
        onDelete={canEditChildTask ? handleDeleteTask : undefined}
        delegates={delegates}
        selectedDelegateUuids={selectedDelegateUuids}
        onDelegatesChange={setSelectedDelegateUuids}
      />

      {isCompactMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <CompactList>
            {renderNewTaskInput()}
            <Droppable droppableId="task-list">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {childTasks.map((item, index) => renderCompactTaskItem(item, index))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </CompactList>
        </DragDropContext>
      ) : (
        <div className="card-group" style={{ padding: '15px 5px' }}>
          <Card.Group stackable doubling>
            {renderNewTaskInput()}
            {childTasks.map(renderCardTaskItem)}
          </Card.Group>
        </div>
      )}
      {childTasks.length === 0 && <p>No child tasks yet.</p>}
    </div>
  );
};

export default DiditList;
